'use client'

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, SyntheticEvent, useEffect, useState } from 'react'

import HeartIcon from '@src/components/product-card/icons/heartIcon.inline.svg'
// Reuse the legacy heart's styles verbatim (generic positioning + icon fills, nothing color-hardcoded);
// fold into the SVD dir when the legacy card is retired (Phase 3 / CSS-parity pass).
import styles from '@src/components/product-card/product-card-actions/add-to-wishlist/add-to-wishlist.module.scss'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { IconButton } from '@src/quince-components/src'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { serializeSelectedAttributes } from '@src/quince-site-common/src/services/Wishlist.types'
import { emitReactNativeEvent } from '@src/quince-site-common/src/utils/crossPlatformEvents'
import { CROSS_PLATFORM_EVENT_TYPES } from '@src/quince-site-common/src/utils/crossPlatformEvents/constants'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { getSvdAnchorAnalyticsFields } from '../svd-anchor-analytics'

import { buildSvdWishlistTrackingDetails, getSvdWishlistEntry } from './svd-wishlist.helpers'
import { useSvdWishlist } from './use-svd-wishlist'

/**
 * SVD-native wishlist heart. Self-contained (Option 2) — no fork of the legacy `add-to-wishlist.tsx`;
 * consumes `SvdNativeProduct`/`SvdNativeCardVariant` directly. Uses the SVD wishlist hook, keyed by
 * `productId + selectedAttributes` (Phase 2): guests persist to the localStorage superset only,
 * logged-in users also persist to the `/v2` backend. Optimistic toggle + native-webview bridge mirror
 * the legacy heart's spid+color fields, plus `productId`/`selectedAttributes` so the hybrid app can key
 * off the SVD identity too (confirmed with app team — they listen to these events to update the app's
 * own wishlist state instead of a full refetch on webview exit; see thread linked in the PR).
 *
 * The card decides whether to render this (gates on `flags.wishlistEnabled` + non-bundle).
 */
export const SvdCardAddToWishlist: FC<{
  product: SvdNativeProduct
  selectedVariant?: SvdNativeCardVariant
  /** Extra tracking context from the section (search/position etc.); merged into the payload. */
  wishlistTrackingConfig?: Record<string, unknown>
  className?: string
  iconClassName?: string
}> = ({ product, selectedVariant, wishlistTrackingConfig = {}, className, iconClassName }) => {
  const { t } = useTranslation('common')
  const [isWishListed, setIsWishListed] = useState(false)
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useSvdWishlist()
  const isWebView = getIsNativeViewClient()

  const entry = getSvdWishlistEntry(product, selectedVariant)
  const { productId, selectedAttributes, spid, color: anchorValue } = entry
  // Stable primitive dep for the effect (selectedAttributes is an object).
  const selectedAttrsKey = serializeSelectedAttributes(selectedAttributes)

  useEffect(() => {
    setIsWishListed(isItemInWishList(productId, selectedAttributes))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedAttrsKey is the stable form of selectedAttributes
  }, [isItemInWishList, productId, selectedAttrsKey])

  const onWishListToggle = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsWishListed(prev => !prev)
    if (isWishListed) {
      if (isWebView) {
        emitReactNativeEvent(CROSS_PLATFORM_EVENT_TYPES.REMOVE_FROM_WISHLIST, {
          spid,
          color: anchorValue,
          productId,
          selectedAttributes,
        })
      }
      removeItemFromWishList(productId, selectedAttributes, {
        ...wishlistTrackingConfig,
        // Anchor-generic VDA keys — parity with the ADD payload's swatch-dimension naming.
        ...getSvdAnchorAnalyticsFields(selectedVariant),
      }).catch((e: unknown) => {
        console.error(e)
      })
    } else {
      if (isWebView) {
        emitReactNativeEvent(CROSS_PLATFORM_EVENT_TYPES.ADD_TO_WISHLIST, {
          spid,
          color: anchorValue,
          productId,
          selectedAttributes,
        })
      }
      addItemToWishList(entry, buildSvdWishlistTrackingDetails(product, selectedVariant, wishlistTrackingConfig)).catch(
        (e: unknown) => {
          console.error(e)
        },
      )
    }
  }

  const anchorName = selectedVariant?.displayConfig?.displayName
  const productName = anchorName ? `${anchorName} ${product.title}` : product.title
  const wishlistAriaLabel = isWishListed
    ? t('pc_remove_wishlist_aria', { productName })
    : t('pc_add_wishlist_aria', { productName })

  return (
    <IconButton
      className={classnames(styles.button, className)}
      iconClassName={classnames(styles.icon, isWishListed ? styles.blackIcon : styles.whiteIcon, iconClassName)}
      iconSvg={HeartIcon}
      onClick={onWishListToggle}
      ariaLabel={wishlistAriaLabel}
      dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
      aria-pressed={isWishListed}
    />
  )
}
